import { useState } from 'react';
import { Alert, CircularProgress, IconButton, useTheme } from '@mui/material';
import { Tooltip } from '@/components/forms/other/Tooltip/Tooltip';
import { IUserNotification } from '@/types/types';
import { Icon } from '@/components/general/Icon/Icon';
import { usePhrase } from '@/hooks';
import { addMinutes } from 'date-fns';
import { AlertColor } from '@mui/material/Alert/Alert';
import { NotificationStyleEnum } from '@/types/enums/notification/notification-style.enum';
import { accountService } from '@/services';
import { ACCOUNT_NOTIFICATIONS_PHRASES } from '@/consts/phrases/accountPhrases';

interface NotificationProps {
    notification: IUserNotification;
    removeSnoozedNotification: () => void;
    removeDismissedNotification: () => void;
}

type NotificationProperty = {
    severity: AlertColor;
    icon: React.ReactNode;
    color: string;
    colorClassName: string;
};

export const Notification = ({
    notification,
    removeSnoozedNotification,
    removeDismissedNotification,
}: NotificationProps) => {
    const { t } = usePhrase();

    const theme = useTheme();

    const createSnoozedNotificationCookie = () => {
        if (typeof window === 'undefined') return;
        const expires: Date = addMinutes(new Date(), notification.snoozeForMin ?? 10);
        document.cookie = `snoozed_${notification.assignmentId}=${
            notification.assignmentId
        }; expires=${expires.toUTCString()}`;
    };

    const [isButtonDisabled, setButtonIsDisabled] = useState(false);

    const getNotificationProperties = () => {
        const properties: NotificationProperty = {
            severity: 'success',
            icon: <Icon path="alertIcons/SuccessIcon" />,
            color: theme.palette.success.main,
            colorClassName: '',
        };

        switch (notification.style) {
            case NotificationStyleEnum.DANGER:
                properties.severity = 'error';
                properties.icon = <Icon path="alertIcons/ErrorIcon" />;
                properties.color = theme.palette.error.main;
                properties.colorClassName = 'notificationAlertError';
                break;
            case NotificationStyleEnum.INFO:
                properties.severity = 'info';
                properties.icon = <Icon path="alertIcons/InfoIcon" />;
                properties.color = theme.palette.info.main;
                properties.colorClassName = 'notificationAlertInfo';
                break;
            case NotificationStyleEnum.WARNING:
                properties.severity = 'warning';
                properties.icon = <Icon path="alertIcons/WarningIcon" />;
                properties.color = theme.palette.warning.main;
                properties.colorClassName = 'notificationAlertWarning';
                break;
            default:
                return properties;
        }

        return properties;
    };

    const notificationProperties = getNotificationProperties();

    const handleSnooze = () => {
        createSnoozedNotificationCookie();
        removeSnoozedNotification();
    };

    const handleDismiss = async () => {
        setButtonIsDisabled(true);
        await accountService.dismissNotification(notification.assignmentId);
        removeDismissedNotification();
    };

    const getToolTipPlacement = (isNotificationDismissible: boolean): 'right' | 'left' => {
        if (isNotificationDismissible) return 'left';
        return 'right';
    };

    const NotificationActionButton =
        notification.isSnoozable || notification.isDismissible ? (
            <>
                {notification.isSnoozable && (
                    <Tooltip
                        title={t(ACCOUNT_NOTIFICATIONS_PHRASES.SNOOZE)}
                        placement={getToolTipPlacement(notification.isDismissible)}
                        enterTouchDelay={0}
                    >
                        <IconButton
                            aria-label="snooze"
                            onClick={handleSnooze}
                            color={notificationProperties.severity}
                            size="small"
                        >
                            {
                                <Icon
                                    path="alertIcons/SnoozeIcon"
                                    color={notificationProperties.color}
                                />
                            }
                        </IconButton>
                    </Tooltip>
                )}
                {notification.isDismissible && (
                    <IconButton
                        aria-label="dismiss"
                        onClick={handleDismiss}
                        color={notificationProperties.severity}
                        size="small"
                        disabled={isButtonDisabled}
                    >
                        {isButtonDisabled ? (
                            <CircularProgress color="inherit" sx={{ padding: '4px' }} size="24px" />
                        ) : (
                            <Tooltip
                                title={t(ACCOUNT_NOTIFICATIONS_PHRASES.CLOSE)}
                                placement="right"
                                enterTouchDelay={0}
                            >
                                {
                                    <Icon
                                        path="alertIcons/DismissIcon"
                                        color={notificationProperties.color}
                                    />
                                }
                            </Tooltip>
                        )}
                    </IconButton>
                )}
            </>
        ) : null;

    return (
        <Alert
            variant="outlined"
            icon={notificationProperties.icon}
            className={`notificationAlert ${notificationProperties.colorClassName}`}
            severity={notificationProperties.severity}
            action={NotificationActionButton}
        >
            {t(notification.phraseId)}
        </Alert>
    );
};
